import React from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
  Legend,
} from "recharts";
import { get_t_str } from "../../../../helpers/helpers";
import { get_t_str_shortened } from "../../../../helpers/helpers";

const AdvancedForecastLineChart = (props) => {
  const hasActualData = props.hasActualData;

  // Accept both the legacy {total_value} shape and the split series shape.
  const chartData = props.data.map((item) => ({
    qh_num: item.qh_num,
    forecast_value: item.forecast_value ?? item.total_value ?? 0,
    actual_value: hasActualData ? item.actual_value ?? null : undefined,
  }));

  // Find peak forecast value for annotation
  const peakForecast = [...chartData].sort(
    (a, b) => (b.forecast_value || 0) - (a.forecast_value || 0)
  )[0];

  // Find peak actual value if available
  const peakActual = hasActualData
    ? [...chartData.filter((d) => d.actual_value != null)].sort(
        (a, b) => (b.actual_value || 0) - (a.actual_value || 0)
      )[0]
    : null;

  // Calculate the max Y value using the greater of forecast or actual
  const maxForecastValue = peakForecast?.forecast_value || 0;
  const maxActualValue = peakActual?.actual_value || 0;
  const maxYValue = Math.max(maxForecastValue, maxActualValue);

  // Find average values
  const avgForecast =
    chartData
      .filter((d) => d.forecast_value != null)
      .reduce((sum, item) => sum + (item.forecast_value || 0), 0) /
    chartData.filter((d) => d.forecast_value != null).length;

  const avgActual = hasActualData
    ? chartData
        .filter((d) => d.actual_value != null)
        .reduce((sum, item) => sum + (item.actual_value || 0), 0) /
      chartData.filter((d) => d.actual_value != null).length
    : 0;

  // Violet-700 from Tailwind color palette (#6d28d9)
  const primaryColor = "#6d28d9";
  const lightPrimaryColor = "#8b5cf6"; // Violet-500
  const darkPrimaryColor = "#5b21b6"; // Violet-800

  // Teal-600 from Tailwind color palette (#0d9488)
  const actualColor = "#0d9488";
  const lightActualColor = "#14b8a6"; // Teal-500
  const darkActualColor = "#0f766e"; // Teal-700

  // Reduce the number of tick labels so they don't overlap
  const tickInterval = 2; // show a tick every 2 hours (8 quarter hours)
  const tickValues = chartData
    .map((item) => item.qh_num)
    .filter((_, index) => index % tickInterval === 0);

  return (
    <div className="flex flex-col items-center w-full rounded-lg">
      <div className="w-full h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 24, bottom: 10, left: 32, right: 32 }}
          >
            <defs>
              <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8} />
                <stop offset="95%" stopColor={primaryColor} stopOpacity={0.1} />
              </linearGradient>
              {hasActualData && (
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={actualColor} stopOpacity={0.8} />
                  <stop
                    offset="95%"
                    stopColor={actualColor}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              )}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
            />
            <XAxis
              dataKey="qh_num"
              tickFormatter={get_t_str_shortened}
              ticks={tickValues}
              stroke="#6b7280"
              tickMargin={10}
              interval={0}
              tick={{ fontSize: 14, fill: "#6b7280" }}
            />
            <YAxis
              width={80}
              domain={[0, maxYValue + 50]}
              stroke="#6b7280"
              tickMargin={10}
              tickFormatter={(value) => Math.round(value)}
            />
            <Tooltip
              formatter={(value, name) => [
                `$${(Math.round(value * 100) / 100).toFixed(0)}`,
                name,
              ]}
              labelFormatter={(qh_num) => `Time: ${get_t_str(qh_num)}`}
              contentStyle={{
                backgroundColor: "#ffffff",
                borderRadius: "4px",
                padding: "8px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
                border: "1px solid #e5e7eb",
              }}
            />

            {/* Forecast Area */}
            <Area
              type="monotone"
              dataKey="forecast_value"
              name="Forecast"
              stroke={primaryColor}
              strokeWidth={2.5}
              fillOpacity={1}
              fill="url(#colorForecast)"
              activeDot={{
                r: 6,
                fill: darkPrimaryColor,
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
            />

            {/* Actual Sales Area */}
            {hasActualData && (
              <Area
                type="monotone"
                dataKey="actual_value"
                name="Actual"
                stroke={actualColor}
                strokeWidth={2.5}
                fillOpacity={0.5}
                fill="url(#colorActual)"
                activeDot={{
                  r: 6,
                  fill: darkActualColor,
                  stroke: "#ffffff",
                  strokeWidth: 2,
                }}
              />
            )}

            {/* Average Forecast Line - only show when no actual data */}
            {!hasActualData && (
              <ReferenceLine
                y={avgForecast}
                stroke="#9ca3af"
                strokeDasharray="3 3"
                label={{
                  value: "Average",
                  position: "insideBottomRight",
                  fill: "#6b7280",
                  fontSize: 12,
                }}
              />
            )}

            {/* Peak Forecast Marker */}
            <ReferenceLine
              x={peakForecast?.qh_num}
              stroke="#dc2626"
              strokeDasharray="3 3"
              strokeWidth={2}
              label={{
                value: !hasActualData ? "Peak" : "",
                position: "top",
                fill: "#dc2626",
                fontSize: 12,
                dy: 0,
                dx: 0,
                background: {
                  fill: "white",
                  padding: 3,
                  radius: 3,
                },
              }}
              ifOverflow="extendDomain"
            />

            {hasActualData && peakActual && (
              <ReferenceLine
                x={peakActual?.qh_num}
                stroke="#ea580c"
                strokeDasharray="3 3"
                strokeWidth={2}
                label={{
                  value: "",
                  position: "top",
                  fill: "#ea580c",
                  fontSize: 12,
                  dy: -10,
                  dx: 0,
                  background: {
                    fill: "white",
                    padding: 3,
                    radius: 3,
                  },
                }}
                ifOverflow="extendDomain"
              />
            )}

            {/* {hasActualData && (
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{
                  paddingTop: '10px'
                }}
              />
            )} */}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full flex justify-center text-sm text-gray-500 mt-4">
        <div className="flex items-center space-x-12">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-violet-700 mr-2"></div>
            <span>Forecast</span>
          </div>

          {hasActualData && (
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-teal-600 mr-2"></div>
              <span>Actual</span>
            </div>
          )}

          {!hasActualData && (
            <div className="flex items-center">
              <div className="w-3 h-0.5 bg-gray-400 mr-2 border-dashed"></div>
              <span>Avg: ${Math.round(avgForecast * 10) / 10}</span>
            </div>
          )}

          <div className="flex items-center">
            <div className="w-3 h-0.5 bg-red-600 mr-2 border-dashed"></div>
            <span>
              {!hasActualData ? "Peak:" : "Peak Forecast"} $
              {(Math.round(peakForecast?.forecast_value * 10) / 10).toFixed()}{" "}
              at {get_t_str_shortened(peakForecast?.qh_num)}
            </span>
          </div>

          {hasActualData && peakActual && (
            <div className="flex items-center">
              <div className="w-3 h-0.5 bg-orange-600 mr-2 border-dashed"></div>
              <span>
                Peak Actual: ${Math.round(peakActual?.actual_value || 0)} at{" "}
                {get_t_str_shortened(peakActual?.qh_num)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedForecastLineChart;
